import { Calendar, ChevronRight, X } from 'lucide-react';
import { parseMoney } from './merchantUtils';
import { sumDeliveryFees } from '../../services/deliveryFees';

function getDayRange(date) {
  const from = new Date(date);
  from.setHours(0, 0, 0, 0);
  const to = new Date(date);
  to.setHours(23, 59, 59, 999);
  return { from, to };
}

function getWeekDays() {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - mondayOffset);
  monday.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + index);
    return day;
  });
}

function getMonthWeeks() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  lastDay.setHours(23, 59, 59, 999);
  const weeks = [];
  let weekStart = new Date(firstDay);
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
  weekStart.setHours(0, 0, 0, 0);

  while (weekStart <= lastDay) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    weeks.push({ start: new Date(weekStart), end: new Date(weekEnd) });
    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + 7);
  }

  return weeks;
}

function formatDayLabel(date) {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return 'Hier';
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function MerchantBilanView({
  bilanPeriode,
  bilanDeliveries,
  periode,
  setPeriode,
  customRange,
  setCustomRange,
  periodeLabel,
  getRefDate,
  setBilanFilter,
  setBilanDrillDate,
  storeFees = null,
}) {
  // Sans aucune commande sur la période, il n'y a pas de taux : le faire
  // retomber sur 0 l'affichait en rouge, comme si tout avait échoué.
  const aDesCommandes = bilanPeriode.nbTotal > 0;
  const tauxLivraison = aDesCommandes
    ? Math.round((bilanPeriode.nbLivrees / bilanPeriode.nbTotal) * 100)
    : null;

  // La période personnalisée est un choix de période comme les autres : elle
  // prend sa place dans le sélecteur au lieu d'un bouton à part.
  const periodeSummaries = [
    { id: 'jour', label: "Aujourd'hui" },
    { id: 'semaine', label: 'Semaine' },
    { id: 'mois', label: 'Ce mois' },
    { id: 'custom', label: 'Période', Icon: Calendar },
  ];
  const weekDays = periode === 'semaine' ? getWeekDays() : [];
  const monthWeeks = periode === 'mois' ? getMonthWeeks() : [];
  const countInRange = (from, to) => bilanDeliveries.filter((delivery) => {
    if (delivery.status !== 'delivered') return false;
    const date = getRefDate(delivery);
    return date >= from && date <= to;
  });

  return (
    <>
      {/* Simple sélecteur : les montants vivaient ici ET dans la carte en
          dessous, qui les redonnait sous un autre nom. */}
      <div className="tab-strip" style={{ marginBottom: '12px' }}>
        {periodeSummaries.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setPeriode(id)}
            className={`tab-btn${periode === id ? ' active' : ''}`}
          >
            {Icon && <Icon size={12} />} {label}
          </button>
        ))}
      </div>

      {periode === 'custom' ? (
        <div style={{ background: 'white', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', border: '1px solid #eef0f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Période personnalisée</span>
            {(customRange.from || customRange.to) && (
              <button onClick={() => setCustomRange({ from: '', to: '' })} style={{ fontSize: '11px', color: '#be123c', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                <X size={11} /> Effacer
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {[{ key: 'from', label: 'Du' }, { key: 'to', label: 'Au' }].map(({ key, label }) => (
              <label key={key} style={{ flex: 1, display: 'block', position: 'relative', cursor: 'pointer' }}>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 12px', border: `1.5px solid ${customRange[key] ? '#173b78' : '#e2e8f0'}`, borderRadius: '10px', background: customRange[key] ? '#f0f4ff' : '#f8fafc', pointerEvents: 'none' }}>
                  <Calendar size={13} color={customRange[key] ? '#173b78' : '#cbd5e1'} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: customRange[key] ? '#173b78' : '#94a3b8' }}>
                    {customRange[key] ? new Date(`${customRange[key]}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : 'Choisir…'}
                  </span>
                </div>
                <input
                  type="date"
                  value={customRange[key]}
                  onChange={(e) => setCustomRange((range) => ({ ...range, [key]: e.target.value }))}
                  style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer', top: '22px' }}
                />
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ background: 'linear-gradient(135deg, #07162d 0%, #112c52 100%)', borderRadius: '20px', padding: '22px 20px', marginBottom: '10px', boxShadow: '0 8px 28px rgba(10,22,40,0.28)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(197,58,45,0.08)' }} />
        <div style={{ position: 'absolute', bottom: '-20px', left: '-20px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700, marginBottom: '4px' }}>
          Chiffre d'affaires · {periodeLabel}
        </div>
        <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '40px', fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: '4px' }}>
          {bilanPeriode.ca.toLocaleString('fr-FR')}
          <span style={{ fontSize: '15px', fontWeight: 600, color: 'rgba(255,255,255,0.35)', marginLeft: '6px' }}>FCFA</span>
        </div>
        {/* Le nombre de livraisons figurait sur les cartes de période ; il
            reste ici, à côté du montant qu'il explique. */}
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontWeight: 600 }}>
          {bilanPeriode.nbLivrees} livraison{bilanPeriode.nbLivrees !== 1 ? 's' : ''}
        </div>
        <div style={{ marginBottom: '16px' }} />
        <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', marginBottom: '16px' }} />
        {/* Deux colonnes posées à même la carte. Les encadrés translucides
            étaient le seul endroit de l'app à employer ce registre, et un
            cadre dans un cadre n'ajoutait rien : la ligne de séparation
            au-dessus suffit à détacher le détail du total. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {[
            { label: 'Bénéfice net',   value: bilanPeriode.net,   color: '#4ade80' },
            { label: 'Frais livraison', value: bilanPeriode.frais, color: 'rgba(255,255,255,0.65)' },
          ].map((item) => (
            <div key={item.label}>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '1.2px', fontWeight: 700, marginBottom: '6px' }}>
                {item.label}
              </div>
              <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '22px', fontWeight: 800, color: item.color, lineHeight: 1 }}>
                {item.value.toLocaleString('fr-FR')}
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.25)', marginLeft: '5px' }}>FCFA</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #eef0f6', padding: '16px 18px', marginBottom: '10px', boxShadow: '0 2px 8px rgba(19,40,72,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: aDesCommandes ? '10px' : 0 }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Taux de livraison</span>
          <span style={{
            fontFamily: aDesCommandes ? 'Alan Sans, sans-serif' : 'inherit',
            fontSize: aDesCommandes ? '22px' : '12px',
            fontWeight: aDesCommandes ? 800 : 600,
            color: !aDesCommandes ? '#94a3b8'
              : tauxLivraison >= 80 ? '#15803d'
              : tauxLivraison >= 50 ? '#c2410c'
              : '#c53a2d',
          }}>
            {aDesCommandes ? `${tauxLivraison}%` : 'Aucune commande'}
          </span>
        </div>
        {aDesCommandes && (
          <>
            <div style={{ height: '10px', borderRadius: '5px', background: '#f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${tauxLivraison}%`, background: tauxLivraison >= 80 ? 'linear-gradient(90deg, #15803d, #16a34a)' : tauxLivraison >= 50 ? 'linear-gradient(90deg, #c2410c, #ea580c)' : 'linear-gradient(90deg, #c53a2d, #8b1e16)', borderRadius: '5px', transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>{bilanPeriode.nbLivrees} livrées</span>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>{bilanPeriode.nbTotal} total</span>
            </div>
          </>
        )}
      </div>

      {/* Même langage que les onglets Commandes et le tableau de bord admin :
          carte claire, liseré coloré, illustration de marque. */}
      <div className="stat-tiles" style={{ marginBottom: '10px' }}>
        {[
          { label: 'Livrées',    value: bilanPeriode.nbLivrees, accent: '#16a34a', img: '/images/stat-commandes-confirmees.webp', alt: 'Commandes livrées',    filter: 'delivered' },
          { label: 'En transit', value: bilanPeriode.nbTransit, accent: '#173b78', img: '/images/stat-en-transit.webp',           alt: 'Commandes en transit', filter: 'transit' },
          { label: 'En attente', value: bilanPeriode.nbAttente, accent: '#c2410c', img: '/images/stat-commandes-a-traiter.webp',  alt: 'Commandes en attente', filter: 'pending' },
          { label: 'Retours',    alert: true, value: bilanPeriode.nbEchec,   accent: '#c53a2d', img: '/images/stat-commandes-retours.webp',    alt: 'Commandes retournées', filter: 'failed' },
        ].map((item) => {
          const clickable = item.value > 0;
          return (
            <button
              key={item.label}
              type="button"
              className="stat-tile"
              onClick={() => clickable && setBilanFilter(item.filter)}
              style={{ cursor: clickable ? 'pointer' : 'default' }}
            >
              <span className={`stat-tile-bar${item.alert ? ' stat-tile-bar--alert' : ''}`} />
              <img className="stat-tile-img" src={item.img} alt={item.alt} loading="lazy" width="44" height="44" />
              <span className="stat-tile-text">
                <span className="stat-tile-value" style={{ color: clickable ? item.accent : '#94a3b8' }}>{item.value}</span>
                <span className="stat-tile-label">{item.label}</span>
              </span>
              {clickable && <ChevronRight size={14} color="#cbd5e1" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
            </button>
          );
        })}
      </div>

      {periode === 'semaine' && weekDays.length > 0 && (
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #eef0f6', padding: '16px 18px', boxShadow: '0 2px 8px rgba(19,40,72,0.05)' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Détail par jour</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {weekDays.map((day) => {
              const { from, to } = getDayRange(day);
              const orders = countInRange(from, to);
              const ca = orders.reduce((sum, delivery) => sum + parseMoney(delivery.amount), 0);
              const net = ca - sumDeliveryFees(orders, storeFees);
              const isToday = day.toDateString() === new Date().toDateString();
              const isFuture = day > new Date();
              return (
                <div key={day.toISOString()} onClick={() => !isFuture && orders.length > 0 && setBilanDrillDate({ from, to, label: formatDayLabel(day) })} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: isToday ? '#f0f4ff' : '#f8fafc', border: `1px solid ${isToday ? '#bfdbfe' : '#eef0f6'}`, cursor: orders.length > 0 ? 'pointer' : 'default', opacity: isFuture ? 0.35 : 1 }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: isToday ? '#173b78' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: isToday ? 'white' : '#64748b' }}>{day.getDate()}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: isToday ? 700 : 500, color: '#0f172a' }}>{formatDayLabel(day)}</div>
                    {orders.length > 0 && <div style={{ fontSize: '11px', color: '#64748b' }}>{orders.length} livraison{orders.length > 1 ? 's' : ''}</div>}
                    {orders.length === 0 && !isFuture && <div style={{ fontSize: '11px', color: '#d1d5db' }}>Aucune livraison</div>}
                  </div>
                  {orders.length > 0 && (
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#173b78' }}>{ca.toLocaleString('fr-FR')} F</div>
                      <div style={{ fontSize: '10px', color: '#4ade80', fontWeight: 700 }}>+{net.toLocaleString('fr-FR')} net</div>
                    </div>
                  )}
                  {orders.length > 0 && <ChevronRight size={14} color="#94a3b8" style={{ flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {periode === 'mois' && monthWeeks.length > 0 && (
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #eef0f6', padding: '16px 18px', boxShadow: '0 2px 8px rgba(19,40,72,0.05)' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>Détail par semaine</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {monthWeeks.map((week, index) => {
              const orders = countInRange(week.start, week.end);
              const ca = orders.reduce((sum, delivery) => sum + parseMoney(delivery.amount), 0);
              const net = ca - sumDeliveryFees(orders, storeFees);
              const isCurrent = new Date() >= week.start && new Date() <= week.end;
              const isFuture = week.start > new Date();
              const weekLabel = `${week.start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} → ${week.end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
              const subDays = Array.from({ length: 7 }, (_, dayIndex) => {
                const day = new Date(week.start);
                day.setDate(week.start.getDate() + dayIndex);
                return day;
              });
              return (
                <div key={`${week.start.toISOString()}-${week.end.toISOString()}`} onClick={() => !isFuture && orders.length > 0 && setBilanDrillDate({ from: week.start, to: week.end, label: `Sem. ${weekLabel}`, subDays })} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: isCurrent ? '#f0f4ff' : '#f8fafc', border: `1px solid ${isCurrent ? '#bfdbfe' : '#eef0f6'}`, cursor: orders.length > 0 ? 'pointer' : 'default', opacity: isFuture ? 0.35 : 1 }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: isCurrent ? '#173b78' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '10px', fontWeight: 800, color: isCurrent ? 'white' : '#64748b' }}>S{index + 1}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: isCurrent ? 700 : 500, color: '#0f172a' }}>{weekLabel}</div>
                    {orders.length > 0 && <div style={{ fontSize: '11px', color: '#64748b' }}>{orders.length} livraison{orders.length > 1 ? 's' : ''}</div>}
                    {orders.length === 0 && !isFuture && <div style={{ fontSize: '11px', color: '#d1d5db' }}>Aucune livraison</div>}
                  </div>
                  {orders.length > 0 && (
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#173b78' }}>{ca.toLocaleString('fr-FR')} F</div>
                      <div style={{ fontSize: '10px', color: '#4ade80', fontWeight: 700 }}>+{net.toLocaleString('fr-FR')} net</div>
                    </div>
                  )}
                  {orders.length > 0 && <ChevronRight size={14} color="#94a3b8" style={{ flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
